import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import plantService from "../../../services/plantService";
import siteLayoutService from "../../../services/siteLayoutService";
import userService from "../../../services/userService";
import { authService } from "../../../services";
import SharedModal from "../../../components/common/SharedModal";
import PageLoading from "../../../components/common/PageLoading";
import "./SiteLayout.css";
import {
  HiOutlineBuildingOffice2,
  HiOutlineGlobeAlt,
  HiOutlineCog6Tooth,
  HiOutlineRectangleStack,
  HiOutlineMagnifyingGlass,
  HiOutlinePlus,
  HiOutlineChevronLeft,
  HiOutlineChevronRight,
  HiOutlineEllipsisVertical,
  HiOutlineXMark,
  HiOutlineMapPin,
  HiOutlinePencil,
  HiOutlineTrash,
} from "react-icons/hi2";

const SiteLayout = () => {
  const [activeTab, setActiveTab] = useState("site_layout");
  const [sites, setSites] = useState([]);
  const [selectedSiteId, setSelectedSiteId] = useState("");
  const [applyToAll, setApplyToAll] = useState(false);
  const [loading, setLoading] = useState(false);
  const [layout, setLayout] = useState({
    id: 0,
    level1: "",
    level2: "",
    level3: "",
    level4: "",
    level5: "",
    level6: "",
  });

  // Facility Items State
  const [levelItems, setLevelItems] = useState([]);
  const [parentLevelItems, setParentLevelItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("add");
  const [currentItem, setCurrentItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    head: "",
    parentId: "",
    status: true,
    latitude: 0,
    longitude: 0,
  });

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Action Menu State
  const [openMenuId, setOpenMenuId] = useState(null);

  useEffect(() => {
    fetchSites();
    fetchUsers();

    // Close action menu when clicking outside
    const handleClickOutside = (event) => {
      if (!event.target.closest(".action-menu-container")) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (activeTab === "site_layout") {
      if (applyToAll) {
        fetchGlobalLayout();
      } else if (selectedSiteId) {
        fetchSiteLayout(selectedSiteId);
      } else {
        resetLayout();
      }
    } else if (selectedSiteId) {
      if (!layout.level1) fetchSiteLayout(selectedSiteId);
      setSearchTerm("");
      setCurrentPage(1);
      setSelectedRows([]);
      fetchLevelItems();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSiteId, applyToAll, activeTab]);

  const fetchSites = async () => {
    try {
      const data = await plantService.getAllPlants();
      const plants = data || [];
      setSites(plants);
      
      // Pre-select plant if only 1 exists
      if (plants.length === 1 && !selectedSiteId) {
        const onlyPlantId = plants[0].plantId || plants[0].id;
        if (onlyPlantId) setSelectedSiteId(String(onlyPlantId));
      }
    } catch (error) {
      console.error("Failed to fetch sites", error);
    }
  };

  const fetchUsers = async () => {
    try {
      const data = await userService.getAllUsers();
      setUsers(data || []);
    } catch (error) {
      console.error("Failed to fetch users", error);
    }
  };

  const fetchSiteLayout = async (siteId) => {
    setLoading(true);
    try {
      const data = await siteLayoutService.getSiteLayout(siteId);
      if (data && (data.siteId || data.plantId || data.id)) {
        setLayout({
          id: data.id || data.siteLayoutId || data.SiteLayoutId || 0,
          level1: data.level1 || "",
          level2: data.level2 || "",
          level3: data.level3 || "",
          level4: data.level4 || "",
          level5: data.level5 || "",
          level6: data.level6 || "",
        });
      } else {
        // Fallback to global only if site-specific layout doesn't exist at all
        await fetchGlobalLayout();
      }
    } catch (error) {
      console.error("Failed to fetch site layout", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchGlobalLayout = async () => {
    setLoading(true);
    try {
      const data = await siteLayoutService.getGlobalLayout();
      if (data) setLayout({ ...data });
    } catch (error) {
      console.error("Failed to fetch global layout", error);
    } finally {
      setLoading(false);
    }
  };

  const resetLayout = () => {
    setLayout({
      id: 0,
      level1: "",
      level2: "",
      level3: "",
      level4: "",
      level5: "",
      level6: "",
    });
  };

  const getLevelFromTab = () => {
    if (!activeTab.startsWith("facility_")) return 0;
    return parseInt(activeTab.split("_")[1]);
  };

  const fetchLevelItems = useCallback(async () => {
    const level = getLevelFromTab();
    if (!level || !selectedSiteId) return;

    setLoading(true);
    try {
      const items = await siteLayoutService.getLevelItems(
        selectedSiteId,
        level,
      );
      // Normalize items to ensure 'id' property exists
      const normalizedItems = (items || []).map((item) => {
        let idVal = item.id || item.itemId || item.Id || item.ItemId;
        if (!idVal) {
          const idKey = Object.keys(item).find(
            (key) =>
              key.toLowerCase().endsWith("id") &&
              ![
                "tenantid",
                "plantid",
                "parentid",
                "siteid",
                "headuserid",
              ].includes(key.toLowerCase()),
          );
          if (idKey) idVal = item[idKey];
        }
        return {
          ...item,
          id: idVal,
        };
      });
      setLevelItems(normalizedItems);

      // Fetch parent level items for dropdown (level > 1)
      if (level > 1) {
        const parentItems = await siteLayoutService.getLevelItems(
          selectedSiteId,
          level - 1,
        );
        const normalizedParents = (parentItems || []).map((item) => {
          let idVal = item.id || item.itemId || item.Id || item.ItemId;
          if (!idVal) {
            const idKey = Object.keys(item).find(
              (key) =>
                key.toLowerCase().endsWith("id") &&
                ![
                  "tenantid",
                  "plantid",
                  "parentid",
                  "siteid",
                  "headuserid",
                ].includes(key.toLowerCase()),
            );
            if (idKey) idVal = item[idKey];
          }
          return {
            ...item,
            id: idVal,
          };
        });
        setParentLevelItems(normalizedParents);
      } else {
        setParentLevelItems([]);
      }
    } catch (error) {
      console.error("Failed to fetch level items", error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedSiteId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setLayout((prev) => {
      const newLayout = { ...prev, [name]: value };

      // If we are clearing a level, clear all subsequent levels too
      if (!value || value.trim() === "") {
        const levelNum = parseInt(name.replace("level", ""), 10);
        for (let i = levelNum + 1; i <= 6; i++) {
          newLayout[`level${i}`] = "";
        }
      }
      return newLayout;
    });
  };

  const handleSaveLayout = async () => {
    if (!applyToAll && !selectedSiteId) {
      toast.warn("Please select a site");
      return;
    }
    setLoading(true);
    try {
      await siteLayoutService.saveSiteLayout(
        selectedSiteId,
        layout,
        applyToAll,
      );
      toast.success("Site Layout saved successfully!");
    } catch (err) {
      console.error("Failed to save layout", err);
      // Let's fall back to reading response error if available
      let errorMsg = "Unknown error";
      if (err.response?.data) {
        if (typeof err.response.data === "string") {
          errorMsg = err.response.data;
        } else if (err.response.data.errors) {
          errorMsg = Object.values(err.response.data.errors).flat().join("\n");
        } else if (err.response.data.message) {
          errorMsg = err.response.data.message;
        }
      } else if (err.message) {
        errorMsg = err.message;
      }
      toast.error(`Failed to save layout: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  // Search handler
  const handleSearch = async (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setCurrentPage(1);

    const level = getLevelFromTab();
    if (!level || !selectedSiteId) return;

    if (value.trim()) {
      setLoading(true);
      try {
        const items = await siteLayoutService.searchLevelItems(
          selectedSiteId,
          level,
          value,
        );
        // Normalize search results
        const normalizedItems = (items || []).map((item) => ({
          ...item,
          id: item.id || item.itemId || item.Id || item.ItemId,
        }));
        setLevelItems(normalizedItems);
      } catch (error) {
        console.error("Search failed", error);
      } finally {
        setLoading(false);
      }
    } else {
      fetchLevelItems();
    }
  };

  // Modal & Item Actions
  const openModal = (mode, item = null) => {
    setModalMode(mode);
    setCurrentItem(item);
    if (mode === "edit" && item) {
      setFormData({
        plantId: item.plantId || selectedSiteId,
        name: item.name,
        head: item.head || "",
        headUserId: item.headUserId || item.HeadUserId || "",
        parentId: item.parentId || "",
        status: item.status === "Active",
        latitude:
          item.latitude !== null && item.latitude !== undefined
            ? item.latitude
            : 0,
        longitude:
          item.longitude !== null && item.longitude !== undefined
            ? item.longitude
            : 0,
      });
    } else {
      setFormData({
        plantId: selectedSiteId,
        name: "",
        head: "",
        headUserId: "",
        parentId: "",
        status: true,
        latitude: 0,
        longitude: 0,
      });
    }
    setShowModal(true);
  };

  const handleModalSubmit = async () => {
    const level = getLevelFromTab();

    // Validation
    if (!formData.name || !formData.name.trim()) {
      toast.warn("Name is required");
      return;
    }

    // Facility Head is required (assuming it's mandatory)
    if (!formData.headUserId && !formData.head) {
      toast.warn("Facility Head is required");
      return;
    }

    // Parent is required for levels > 1
    if (level > 1 && !formData.parentId) {
      toast.warn(`Please select a ${getLevelName(level - 1)}`);
      return;
    }

    const siteId = selectedSiteId ? parseInt(selectedSiteId) : null;
    if (!siteId && !applyToAll) {
      toast.warn("Site ID is missing. Please select a site.");
      return;
    }

    try {
      const payload = {
        tenantId: authService.getTenantId(),
        siteId: siteId,
        plantId: siteId, // Send both to be safe
        level,
        name: formData.name,
        head: formData.head || null,
        headUserId: formData.headUserId || null,
        parentId: formData.parentId ? parseInt(formData.parentId) : null,
        latitude:
          formData.latitude !== "" && formData.latitude !== null
            ? parseFloat(formData.latitude)
            : null,
        longitude:
          formData.longitude !== "" && formData.longitude !== null
            ? parseFloat(formData.longitude)
            : null,
        status: formData.status ? "Active" : "Inactive",
      };

      console.group("🚀 Debug: Saving Site Layout Item");
      console.log("Payload being sent:", payload);
      console.log("Auth Token:", authService.getToken());
      console.log("Current User:", authService.getCurrentUser());
      console.groupEnd();

      if (modalMode === "add") {
        await siteLayoutService.addLevelItem(payload);
      } else {
        await siteLayoutService.updateLevelItem(currentItem.id, {
          ...payload,
          status: formData.status ? "Active" : "Inactive",
        });
      }
      setShowModal(false);
      fetchLevelItems();
    } catch (error) {
      console.error("Failed to save item:", error);
      // If 401, the interceptor handles redirect, so don't show alert
      if (error.response?.status === 401) {
        return;
      }

      let errorMsg = "Unknown error";
      if (error.response?.data) {
        if (typeof error.response.data === "string") {
          errorMsg = error.response.data;
        } else if (error.response.data.message) {
          errorMsg = error.response.data.message;
        } else if (error.response.data.errors) {
          // Handle standard validation error format
          errorMsg = Object.values(error.response.data.errors)
            .flat()
            .join("\n");
        } else {
          // If it's an object but not in a standard error format, stringify it
          errorMsg = JSON.stringify(error.response.data, null, 2);
        }
      } else if (error.message) {
        errorMsg = error.message;
      }

      toast.error(`Failed to save item: ${errorMsg}`);
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (window.confirm("Are you sure delete this?")) {
      try {
        await siteLayoutService.deleteLevelItem(itemId);
        toast.success("Item deleted successfully.");
        fetchLevelItems();
      } catch (err) {
        console.error("Failed to delete item", err);
        toast.error("Failed to delete item.");
      }
    }
  };

  const handleStatusToggle = async (item) => {
    const newStatus = item.status === "Active" ? "Inactive" : "Active";
    try {
      await siteLayoutService.updateLevelItem(item.id, {
        tenantId: authService.getTenantId(),
        plantId: parseInt(selectedSiteId),
        level: item.level,
        name: item.name,
        head: item.head,
        parentId: item.parentId || null,
        latitude: item.latitude || null,
        longitude: item.longitude || null,
        status: newStatus,
      });
      toast.success(`Status updated to ${newStatus}.`);
      fetchLevelItems();
    } catch (err) {
      console.error("Failed to toggle status", err);
      toast.error("Failed to toggle status.");
    }
  };

  // Geolocation
  const handleGetLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setFormData((prev) => ({
            ...prev,
            latitude: position.coords.latitude.toFixed(6),
            longitude: position.coords.longitude.toFixed(6),
          }));
        },
        (err) => {
          console.error("Geolocation error:", err);
          toast.error("Unable to get location. Please check permissions.");
        },
      );
    } else {
      toast.error("Geolocation is not supported by this browser.");
    }
  };

  // Row selection
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedRows(paginatedItems.map((item) => item.id));
    } else {
      setSelectedRows([]);
    }
  };

  const handleSelectRow = (id) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id],
    );
  };

  // Pagination logic
  const totalItems = levelItems.length;
  const totalPages = Math.ceil(totalItems / pageSize);
  const paginatedItems = levelItems.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize,
  );

  const getLevelName = (levelNum) => {
    return layout[`level${levelNum}`] || `Facility ${levelNum}`;
  };

  if (loading && !sites.length) {
    return <PageLoading message="Loading site configuration..." />;
  }

  return (
    <div className="pagecontainer">
      {/* Page Header */}
      <div className="page-header">
        <div className="header-content">
          <h1>Site Layout</h1>
          <p>Manage site layout and levels</p>
        </div>
      </div>
      {/* Header / Tabs */}
      <div className="tabs-header">
        <div
          className={`tab-item ${activeTab === "site_layout" ? "active" : ""}`}
          onClick={() => setActiveTab("site_layout")}
        >
          Site Layout
        </div>
        {[1, 2, 3, 4, 5, 6].map((num) => {
          const levelName = layout[`level${num}`];
          if (!levelName || levelName.trim() === "") return null;

          return (
            <div
              key={num}
              className={`tab-item ${activeTab === `facility_${num}` ? "active" : ""}`}
              onClick={() => setActiveTab(`facility_${num}`)}
            >
              {levelName}
            </div>
          );
        })}
      </div>

      <div className="tab-content">
        {activeTab === "site_layout" ? (
          /* ==================== SITE LAYOUT TAB ==================== */
          <div className="layout-definition-card">
            <h2 className="section-title">Site Configuration</h2>

            {/* Section 1: Core Config */}
            <div className="inner-card core-config-card">
              <div className="core-config-header">
                <div className="config-icon-wrapper">
                  <HiOutlineCog6Tooth className="config-icon" />
                </div>
                <div className="config-title-group">
                  <h3>Core Configuration</h3>
                  <p>Manage site-specific or global layout settings</p>
                </div>
              </div>

              <div className="core-config-body">
                <div className="config-field-group">
                  <label className="config-label">
                    <HiOutlineBuildingOffice2 className="label-icon" />
                    Target Site
                  </label>
                  <div className="select-wrapper-styled">
                    <select
                      className="form-select-styled"
                      value={selectedSiteId}
                      onChange={(e) => {
                        const value = e.target.value;
                        setSelectedSiteId(value);
                      }}
                      disabled={applyToAll}
                    >
                      <option value="" disabled>
                        Select Site to Configure
                      </option>
                      {sites.map((site) => (
                        <option
                          key={site.plantId || site.id}
                          value={site.plantId || site.id}
                        >
                          {site.plantName || site.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="config-divider"></div>

                <div className="config-field-group toggle-group">
                  <label className="config-label">
                    <HiOutlineGlobeAlt className="label-icon" />
                    Global Configuration
                  </label>
                  <div className="toggle-wrapper">
                    <span
                      className={`toggle-status ${applyToAll ? "active" : ""}`}
                    >
                      {applyToAll
                        ? "Applied to All Sites"
                        : "Specific Site Only"}
                    </span>
                    <label className="toggle-switch-ios">
                      <input
                        type="checkbox"
                        checked={applyToAll}
                        onChange={(e) => setApplyToAll(e.target.checked)}
                      />
                      <span className="slider-ios"></span>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 2: Level Definitions */}
            <div className="inner-card level-config-card">
              <div className="core-config-header">
                <div className="config-icon-wrapper level-icon-wrapper">
                  <HiOutlineRectangleStack className="config-icon" />
                </div>
                <div className="config-title-group">
                  <h3>Level Hierarchy</h3>
                  <p>
                    Define the organizational structure levels for your facility
                  </p>
                </div>
              </div>

              <div className="level-config-body">
                <div className="levels-grid-styled">
                  {[1, 2, 3, 4, 5, 6].map((num) => {
                    const isPreviousEmpty =
                      num > 1 &&
                      (!layout[`level${num - 1}`] ||
                        layout[`level${num - 1}`].trim() === "");
                    return (
                      <div
                        className={`level-input-group ${isPreviousEmpty ? "disabled-group" : ""}`}
                        key={num}
                      >
                        <label className="level-label">
                          <span className="level-badge">{num}</span>
                          <span className="label-text">Level {num}</span>
                        </label>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            width: "100%",
                          }}
                        >
                          <input
                            type="text"
                            className="form-input-styled"
                            name={`level${num}`}
                            placeholder={
                              isPreviousEmpty
                                ? "Define previous level first"
                                : `e.g. ${num === 1 ? "Area" : num === 2 ? "Division" : "Sub-Location"}`
                            }
                            value={layout[`level${num}`] || ""}
                            onChange={handleInputChange}
                            disabled={isPreviousEmpty}
                          />
                          {!isPreviousEmpty && layout[`level${num}`] && (
                            <button
                              type="button"
                              onClick={() =>
                                handleInputChange({
                                  target: { name: `level${num}`, value: "" },
                                })
                              }
                              style={{
                                background: "none",
                                border: "none",
                                color: "#ef4444",
                                cursor: "pointer",
                                padding: "4px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                              title={`Clear Level ${num}`}
                            >
                              <HiOutlineTrash size={20} />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="button-group">
              <button className="btn btn-secondary" onClick={resetLayout}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleSaveLayout}>
                {loading ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        ) : (
          /* ==================== FACILITY TAB ==================== */
          <div className="facility-card">
            {!selectedSiteId ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "100px 40px",
                  background: "linear-gradient(to bottom, #ffffff, #f8fafc)",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    width: "80px",
                    height: "80px",
                    borderRadius: "24px",
                    background: "rgba(99, 102, 241, 0.08)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#6366f1",
                    marginBottom: "24px",
                    boxShadow: "0 8px 16px rgba(99, 102, 241, 0.05)",
                  }}
                >
                  <HiOutlineBuildingOffice2 size={40} />
                </div>
                <h3
                  style={{
                    fontSize: "22px",
                    fontWeight: "800",
                    color: "#0f172a",
                    margin: "0 0 12px 0",
                    letterSpacing: "-0.5px",
                  }}
                >
                  No Site Selected
                </h3>
                <p
                  style={{
                    fontSize: "15px",
                    color: "#64748b",
                    margin: "0",
                    maxWidth: "360px",
                    lineHeight: "1.6",
                  }}
                >
                  Please select a site in the <b>Site Layout</b> tab to start
                  managing its hierarchy and facility details.
                </p>
              </div>
            ) : (
              <>
                <div className="facility-card-header">
                  <h2 className="facility-title-v2">
                    {getLevelName(getLevelFromTab())}
                  </h2>
                  <div className="facility-actions-row">
                    <div className="search-wrapper-v2">
                      <span
                        className="search-icon-v2"
                        style={{ top: "18px", left: "10px" }}
                      >
                        <HiOutlineMagnifyingGlass size={20} />
                      </span>
                      <input
                        type="text"
                        placeholder="Search items..."
                        className="search-input-v2"
                        value={searchTerm}
                        onChange={handleSearch}
                      />
                    </div>
                    <button
                      className="btn-add-v2 btn btn-primary"
                      onClick={() => openModal("add")}
                    >
                      <HiOutlinePlus size={18} />
                      <span>Add New</span>
                    </button>
                  </div>
                </div>

                {loading ? (
                  <div className="loading-spinner">
                    <div className="spinner"></div>
                    <p>Loading records...</p>
                  </div>
                ) : (
                  <div className="modern-table-container">
                    <table className="modern-table">
                      <thead>
                        <tr>
                          <th style={{ width: "60px" }}>
                            <input
                              type="checkbox"
                              className="custom-checkbox-styled"
                              onChange={handleSelectAll}
                              checked={
                                paginatedItems.length > 0 &&
                                selectedRows.length === paginatedItems.length
                              }
                            />
                          </th>
                          <th>Name</th>
                          {getLevelFromTab() > 1 && (
                            <th>{getLevelName(getLevelFromTab() - 1)} Name</th>
                          )}
                          <th className="text-center">Head</th>
                          <th className="text-center">Status</th>
                          <th className="text-right" style={{ width: "100px" }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedItems.length > 0 ? (
                          paginatedItems.map((item) => (
                            <tr
                              key={item.id}
                              className={
                                selectedRows.includes(item.id)
                                  ? "row-selected"
                                  : ""
                              }
                            >
                              <td>
                                <input
                                  type="checkbox"
                                  className="custom-checkbox-styled"
                                  checked={selectedRows.includes(item.id)}
                                  onChange={() => handleSelectRow(item.id)}
                                />
                              </td>
                              <td style={{ fontWeight: 700, color: "#1e293b" }}>
                                {item.name}
                              </td>
                              {getLevelFromTab() > 1 && (
                                <td style={{ color: "#64748b" }}>
                                  {item.parentName || "-"}
                                </td>
                              )}
                              <td className="text-center">
                                {item.head || "-"}
                              </td>
                              <td className="text-center">
                                <label className="switch-styled">
                                  <input
                                    type="checkbox"
                                    checked={item.status === "Active"}
                                    onChange={() => handleStatusToggle(item)}
                                  />
                                  <span className="slider-styled"></span>
                                </label>
                              </td>
                              <td className="text-right">
                                <div
                                  className="action-buttons"
                                  style={{ justifyContent: "flex-end" }}
                                >
                                  <button
                                    className="btn-icon-edit"
                                    onClick={() => openModal("edit", item)}
                                    title="Edit"
                                  >
                                    <HiOutlinePencil size={20} />
                                  </button>
                                  <button
                                    className="btn-icon-delete"
                                    onClick={() => handleDeleteItem(item.id)}
                                    title="Delete"
                                  >
                                    <HiOutlineTrash size={20} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td
                              colSpan={getLevelFromTab() > 1 ? 6 : 5}
                              style={{ padding: "0" }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  padding: "80px 20px",
                                  backgroundColor: "#ffffff",
                                  textAlign: "center",
                                }}
                              >
                                <div
                                  style={{
                                    width: "64px",
                                    height: "64px",
                                    borderRadius: "20px",
                                    backgroundColor: "#f1f5f9",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: "#94a3b8",
                                    marginBottom: "16px",
                                  }}
                                >
                                  <HiOutlineMagnifyingGlass size={32} />
                                </div>
                                <h3
                                  style={{
                                    fontSize: "18px",
                                    fontWeight: "700",
                                    color: "#1e293b",
                                    margin: "0 0 8px 0",
                                  }}
                                >
                                  No records found
                                </h3>
                                <p
                                  style={{
                                    fontSize: "14px",
                                    color: "#64748b",
                                    margin: "0",
                                    maxWidth: "300px",
                                  }}
                                >
                                  We couldn't find any{" "}
                                  {getLevelName(getLevelFromTab())} matching
                                  your search or site selection.
                                </p>
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>

                    {totalItems > 0 && (
                      <div className="pagination-v2">
                        <div className="pagination-stats">
                          Showing {(currentPage - 1) * pageSize + 1} to{" "}
                          {Math.min(currentPage * pageSize, totalItems)} of{" "}
                          {totalItems} entries
                        </div>
                        <div className="pagination-controls-v2">
                          <button
                            className="page-btn-v2"
                            disabled={currentPage === 1}
                            onClick={() =>
                              setCurrentPage((p) => Math.max(1, p - 1))
                            }
                          >
                            <HiOutlineChevronLeft size={16} />
                          </button>

                          {Array.from({ length: totalPages }, (_, i) => i + 1)
                            .filter((page) => {
                              if (totalPages <= 5) return true;
                              return (
                                Math.abs(page - currentPage) <= 1 ||
                                page === 1 ||
                                page === totalPages
                              );
                            })
                            .map((page, index, array) => (
                              <React.Fragment key={page}>
                                {index > 0 && array[index - 1] !== page - 1 && (
                                  <span style={{ color: "#cbd5e1" }}>...</span>
                                )}
                                <button
                                  className={`page-btn-v2 ${currentPage === page ? "active" : ""}`}
                                  onClick={() => setCurrentPage(page)}
                                >
                                  {page}
                                </button>
                              </React.Fragment>
                            ))}

                          <button
                            className="page-btn-v2"
                            disabled={currentPage === totalPages}
                            onClick={() =>
                              setCurrentPage((p) => Math.min(totalPages, p + 1))
                            }
                          >
                            <HiOutlineChevronRight size={16} />
                          </button>

                          <select
                            className="page-size-select-v2"
                            value={pageSize}
                            onChange={(e) => {
                              setPageSize(Number(e.target.value));
                              setCurrentPage(1);
                            }}
                          >
                            <option value={10}>10 / Page</option>
                            <option value={20}>20 / Page</option>
                            <option value={50}>50 / Page</option>
                          </select>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      <SharedModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={`${modalMode === "add" ? "New" : "Edit"} ${getLevelName(getLevelFromTab())}`}
        footer={
          <button className="btn btn-primary" onClick={handleModalSubmit}>
            {modalMode === "add" ? "Create Record" : "Save Changes"}
          </button>
        }
      >
        <div className="shared-modal-body newarea">
          <div className="form-grid">
            {/* Name Field */}
            <div className="form-field">
              <label className="field-label">
                {getLevelName(getLevelFromTab())} Name *
              </label>
              <input
                type="text"
                className="input-styled-v2"
                placeholder={`Select ${getLevelName(getLevelFromTab())}`}
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
              />
            </div>

            {/* Head Field */}
            <div className="form-field">
              <label className="field-label">
                {getLevelName(getLevelFromTab())} Head *
              </label>
              <select
                className="select-styled-v2"
                value={formData.headUserId}
                onChange={(e) => {
                  const userId = e.target.value;
                  const user = users.find(
                    (u) => String(u.userId || u.id) === userId,
                  );
                  setFormData({
                    ...formData,
                    headUserId: userId,
                    head: user ? user.fullName || user.username : "",
                  });
                }}
              >
                <option value="" disabled>
                  Select Head
                </option>
                {users.map((user) => (
                  <option
                    key={user.userId || user.id}
                    value={user.userId || user.id}
                  >
                    {user.fullName || user.username}
                  </option>
                ))}
              </select>
            </div>

            {/* Parent Level Field (Only for Level 2-6) */}
            {getLevelFromTab() > 1 && (
              <div className="form-field-v2">
                <label className="field-label-v2">
                  Parent {getLevelName(getLevelFromTab() - 1)} *
                </label>
                <select
                  className="select-styled-v2"
                  value={formData.parentId}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      parentId: e.target.value,
                    })
                  }
                >
                  <option value="" disabled>
                    Select {getLevelName(getLevelFromTab() - 1)}
                  </option>
                  {parentLevelItems.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Geo Coordinates Row - Styled like the 3-column row in image */}
            <div className="form-row-v2">
              <div className="form-field-v2">
                <label className="field-label-v2">Latitude</label>
                <div className="input-container-v2">
                  <input
                    type="number"
                    className="input-styled-v2"
                    placeholder="0.0000"
                    value={formData.latitude}
                    onChange={(e) =>
                      setFormData({ ...formData, latitude: e.target.value })
                    }
                  />
                </div>
              </div>
              <div className="form-field-v2">
                <label className="field-label-v2">Longitude</label>
                <div className="input-container-v2">
                  <input
                    type="number"
                    className="input-styled-v2"
                    placeholder="0.0000"
                    value={formData.longitude}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        longitude: e.target.value,
                      })
                    }
                  />
                </div>
              </div>
              <div
                className="form-field-v2"
                style={{ justifyContent: "flex-end" }}
              >
                <button
                  className="btn-locate-v2"
                  onClick={handleGetLocation}
                  title="Get Current Location"
                >
                  <HiOutlineMapPin size={22} />
                </button>
              </div>
              <div className="status-toggle-v2">
                <span>STATUS</span>
                <label className="switch-styled">
                  <input
                    type="checkbox"
                    checked={formData.status}
                    onChange={(e) =>
                      setFormData({ ...formData, status: e.target.checked })
                    }
                  />
                  <span className="slider-styled"></span>
                </label>
              </div>
            </div>
          </div>
        </div>
      </SharedModal>
    </div>
  );
};

export default SiteLayout;
